import React, { useState, useEffect } from 'react';
import { Card, List, Tag, Empty, Spin, Typography, Space, Segmented } from 'antd';
import Table from 'components/Common/Table';
import { AppstoreOutlined, UnorderedListOutlined, PushpinOutlined } from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';

const { Text } = Typography;

const linkTypeColors = {
    internal: 'blue',
    external: 'green',
    documentation: 'purple',
    api: 'orange',
    resource: 'cyan',
    tool: 'magenta',
};

const ImportantLinks = ({ employee }) => {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState('grid');

    useEffect(() => {
        if (employee?.id) {
            loadLinks();
        }
    }, [employee?.id]);

    const loadLinks = async () => {
        setLoading(true);
        try {
            const response = await actions.loadMySharedLinks();
            if (response?.data) {
                setLinks(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            console.error('Error loading shared links:', error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: '',
            dataIndex: 'is_pinned',
            key: 'is_pinned',
            width: 40,
            render: (pinned) => pinned ? <PushpinOutlined style={{ color: '#faad14' }} /> : null,
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (title, record) => (
                <a onClick={() => window.open(record.url, '_blank')} style={{ cursor: 'pointer' }}>
                    {title}
                </a>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'short_description',
            key: 'short_description',
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: 'Type',
            dataIndex: 'link_type',
            key: 'link_type',
            width: 120,
            render: (type) => (
                <Tag color={linkTypeColors[type] || 'default'}>{type}</Tag>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category_name',
            key: 'category_name',
            width: 140,
            render: (name) => name || '-',
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Segmented
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                        { value: 'grid', icon: <AppstoreOutlined /> },
                        { value: 'list', icon: <UnorderedListOutlined /> },
                    ]}
                />
            </div>
            <Spin spinning={loading}>
                {links.length === 0 && !loading ? (
                    <Empty description="No links shared with you" />
                ) : viewMode === 'grid' ? (
                    <List
                        grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 4 }}
                        dataSource={links}
                        renderItem={(link) => (
                            <List.Item>
                                <Card
                                    size="small"
                                    hoverable
                                    onClick={() => window.open(link.url, '_blank')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                        <Space size={8} align="start">
                                            {link.is_pinned && (
                                                <PushpinOutlined style={{ color: '#faad14' }} />
                                            )}
                                            <Text strong ellipsis={{ tooltip: link.title }}>
                                                {link.title}
                                            </Text>
                                        </Space>
                                        {link.short_description && (
                                            <Text type="secondary" ellipsis={{ tooltip: link.short_description }}>
                                                {link.short_description}
                                            </Text>
                                        )}
                                        <Space size={4} wrap>
                                            <Tag color={linkTypeColors[link.link_type] || 'default'}>
                                                {link.link_type}
                                            </Tag>
                                            {link.category_name && (
                                                <Tag>{link.category_name}</Tag>
                                            )}
                                        </Space>
                                    </Space>
                                </Card>
                            </List.Item>
                        )}
                    />
                ) : (
                    <Table
                        dataSource={links}
                        columns={columns}
                        rowKey={(row) => row.id}
                        size="small"
                        pagination={false}
                    />
                )}
            </Spin>
        </div>
    );
};

export default ImportantLinks;
